import { AddCircle, CheckCircleOutline, Delete, Edit, History } from '@mui/icons-material';
import WarningIcon from '@mui/icons-material/Warning';
import {
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    Tooltip,
} from '@mui/material';
import MuiSwitch from '@mui/material/Switch';
import { GridActionsCellItem, GridSortModel } from '@mui/x-data-grid-premium';
import { AddDeviations } from 'pages/addDeviations/addDeviations';
import { AddProducts } from 'pages/addProducts/AddProducts';
import { TierSetUpResolver } from 'pages/tierSetUp/tierSetUpResolver';
import { useCallback, useContext, useEffect, useState } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { ROUTES } from 'routing';
import { CustomWidthTooltip } from 'shared/components';
import { GridEditDateCell } from 'shared/components/DataGridEditDateCell';
import { ExportIconDropdownMenu } from 'shared/components/ExportIconDropdownMenu';
import { TableDataPage } from 'shared/components/TableDataPage';
import { MassActionToolbar } from 'shared/components/massActions';
import ChangeByDollarAmount, {
    useChangeByDollarAmountMassAction,
} from 'shared/components/massActions/ChangeByDollarAmount';
import ChangeByPercent, { useChangeByPercentMassAction } from 'shared/components/massActions/ChangeByPercent';
import ChangeBySetValue, { useChangeBySetValueMassAction } from 'shared/components/massActions/ChangeBySetValue';
import ChangeDeviationDate, {
    useChangeDeviationDateMassAction,
} from 'shared/components/massActions/ChangeDeviationDate';
import NoInput, { useNoInputMassAction } from 'shared/components/massActions/NoInput';
import { PriceHistoryDialogProps, PriceHistoryModal } from 'shared/components/priceHistoryModal';
import {
    ApprovalStatusEnum,
    DeviationDateMassActionEnum,
    ManagePricingMarketPriceDeleteMassActionEnum,
    MarketPriceExportEnum,
    ModifyPriceMassActionEnum,
    PERMISSIONS,
} from 'shared/constants';
import { useApproverUsers, useGetEndUserDropdown, useUserPermissions } from 'shared/hooks';
import UseDynamicUrl from 'shared/hooks/useDynamicUrl';
import { useExportPriceSheet } from 'shared/hooks/useExportPriceSheet';
import { useGetPriceHistory } from 'shared/hooks/useGetPriceHistory';
import { useDistributor } from 'shared/providers';
import { ProvideSelectedItems, createSelectedItemsContext } from 'shared/providers/provide-selected-items';
import { EndUserTooltipItem, GridColumns, MenuOption } from 'shared/types';
import {
    determineApprovalLevel,
    formatDatePickerStringWithoutTime,
    formatLocalDateToLocalISODateTimeString,
    formatUtcDateTimeToLocalDateTimeString,
    parseDatePickerDate,
    parseUtcDateTime,
} from 'shared/utility';
import {
    getStatusColumnColor,
    isBetweenNeg1AndInclusivePos1,
    isPositiveOrZero,
    isValidDate,
    moneyFourDecimalFormatter,
} from 'shared/utility/dataGrid';
import { CurrentPricesData, EndUserDropdown, PriceHistoryData, TAddProductsData } from 'types/backend';
import { CurrentPricesLayout } from './currentPricesLayout';
import { CurrentPricesSummary } from './currentPricesSummary';
import { EditDeviationFormDialog } from './editDeviationFormDialog';
import { EditMarketPriceFormDialog } from './editMarketPriceFormDialog';
import { ExportQuoteModal } from './exportQuoteModal';
import { AccountNumberParams, managePricingContext } from './managePricingResolver';

interface DeleteDialogProps {
    open: boolean;
    row?: CurrentPricesData;
}

interface EditMarketPriceDialogProps {
    open: boolean;
    row?: CurrentPricesData;
}

export const selectedProductsContext = createSelectedItemsContext<TAddProductsData>();

export function ManagePricing() {
    const { response, sendRowDelete, massActionModal, filtersApplied } = useContext(managePricingContext);
    const [abortController, setAbortController] = useState<AbortController>(() => new AbortController());
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const priceSheetExportMutation = useExportPriceSheet(abortController.signal);

    const distributor = useDistributor();

    const handlePriceSheetExport = async () => {
        await priceSheetExportMutation.mutateAsync({
            customerNumber: distributor.matchingCustomer?.customerNumber,
            filtersApplied,
        });
        setAbortController(new AbortController());
    };

    const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'startdate', sort: 'desc' }]);

    const handleSortModelChange = (newSortModel: GridSortModel) => {
        setSortModel(newSortModel);
    };

    const {
        permissions,
        isAddProductAllowed,
        isExpireMarketPriceAllowed,
        isMassEditMarketPricesAllowed,
        isMassEditDeviationsAllowed,
    } = useUserPermissions();
    const isUserAdmin = permissions.includes(PERMISSIONS.admin);
    const { data: approverUsers } = useApproverUsers();

    const [editMarketPriceDialogState, setEditMarketPriceDialogState] = useState<EditMarketPriceDialogProps>({
        open: false,
    });

    const [editDeviationDialogState, setEditDeviationDialogState] = useState<EditMarketPriceDialogProps>({
        open: false,
    });
    const managePricingRoute = UseDynamicUrl<AccountNumberParams>({ route: ROUTES.managePricing });
    const [deleteDialogState, setDeleteDialogState] = useState<DeleteDialogProps>({ open: false });
    const [selectedCurrentPrices, setSelectedCurrentPrices] = useState<CurrentPricesData[]>([]);
    const [selectedMassActionType, setSelectedMassActionType] = useState<string>('');
    const [allMarketPriceRowsSelected, setAllMarketPriceRowsSelected] = useState<boolean>(false);
    const [allDeviationRowsSelected, setAllDeviationRowsSelected] = useState<boolean>(false);
    const [priceHistoryDialogState, setPriceHistoryDialogState] = useState<PriceHistoryDialogProps<CurrentPricesData>>({
        open: false,
    });

    const {
        response: priceHistoryResponse,
        serverSideProps: priceHistoryServerSideProps,
        resetPageNumber,
        setTableDataRequest,
    } = useGetPriceHistory(
        priceHistoryDialogState.row ? priceHistoryDialogState.row?.id : '',
        sortModel[0]?.field,
        sortModel[0]?.sort
    );

    useEffect(() => {
        if (sortModel.length > 0) {
            setTableDataRequest({ SortModel: sortModel });
        }
    }, [sortModel, setTableDataRequest]);

    const priceHistoryData: PriceHistoryData[] = priceHistoryResponse?.data?.rows || [];

    const openPriceHistoryDialog = useCallback(
        (price: CurrentPricesData) => {
            setPriceHistoryDialogState({
                open: true,
                row: price,
            });
        },
        [setPriceHistoryDialogState]
    );

    const closePriceHistoryDialog = () => {
        setPriceHistoryDialogState({
            open: false,
            row: undefined,
        });
    };

    const handleClick = (params: CurrentPricesData) => () => {
        if (selectedCurrentPrices.map((s) => s.id).some((s) => s === params.id)) {
            const newSelected = [...selectedCurrentPrices];
            newSelected.splice(
                selectedCurrentPrices.map((s) => s.id).findIndex((s) => s === params.id),
                1
            );
            setSelectedCurrentPrices([...newSelected]);
        } else {
            setSelectedCurrentPrices([...selectedCurrentPrices, params]);
        }
    };

    const endUserDropdownResponse = useGetEndUserDropdown(distributor.matchingCustomer.customerNumber);
    const endUserOptions: EndUserDropdown[] = endUserDropdownResponse.data?.rows || [];

    const handleSuccessMassActionsSubmit = useCallback(() => {
        setSelectedCurrentPrices([]);
    }, [setSelectedCurrentPrices]);

    useEffect(() => {
        handleSuccessMassActionsSubmit();
        setAllMarketPriceRowsSelected(false);
        setAllDeviationRowsSelected(false);
    }, [
        filtersApplied,
        handleSuccessMassActionsSubmit,
        setSelectedCurrentPrices,
        setAllMarketPriceRowsSelected,
        setAllDeviationRowsSelected,
    ]);

    const massActionsProps = {
        controllerName: 'CurrentPricesUI',
        affectedDataContext: managePricingContext,
        selected: selectedCurrentPrices.map((s) => s.id),
        allRowsSelected: allMarketPriceRowsSelected,
        onSuccess: handleSuccessMassActionsSubmit,
    };

    const submitChangeModifyPriceByPercent = useChangeByPercentMassAction(massActionsProps);

    const submitChangeModifyPriceByDollarAmount = useChangeByDollarAmountMassAction(massActionsProps);

    const submitChangeModifyPriceBySetValue = useChangeBySetValueMassAction(massActionsProps);

    const submitChangeDeviationDate = useChangeDeviationDateMassAction({
        ...massActionsProps,
        allRowsSelected: allDeviationRowsSelected,
    });

    const submitNoInputResetToCurrentTier = useNoInputMassAction({
        ...massActionsProps,
        type: ModifyPriceMassActionEnum.RESETTOCURRENTTIER,
    });

    const submitMassDeleteMarketPrices = useNoInputMassAction({
        ...massActionsProps,
        type: ManagePricingMarketPriceDeleteMassActionEnum.DELETE,
    });

    const handleIsEditableRow = useCallback(
        (rowId: string) => {
            const rowData = response?.data?.rows.find((r) => r.id === rowId);
            return rowData?.priceType?.toLowerCase() === 'deviation';
        },
        [response?.data?.rows]
    );

    const openDeleteDialog = (row: CurrentPricesData) => {
        setDeleteDialogState({
            open: true,
            row: row,
        });
    };
    const closeDeleteDialog = () => {
        setDeleteDialogState({
            open: false,
            row: undefined,
        });
    };

    const openEditMarketPriceDialog = (row: CurrentPricesData) => {
        setEditMarketPriceDialogState({
            open: true,
            row: row,
        });
    };

    const openEditDeviationDialog = (row: CurrentPricesData) => {
        setEditDeviationDialogState({
            open: true,
            row: row,
        });
    };

    const closeEditMarketPriceDialog = () => {
        setEditMarketPriceDialogState({
            open: false,
            row: undefined,
        });
    };

    const closeEditDeviationDialog = () => {
        setEditDeviationDialogState({
            open: false,
            row: undefined,
        });
    };

    const confirmDelete = () => {
        if (!!deleteDialogState.row) {
            sendRowDelete(deleteDialogState.row);
        }
        closeDeleteDialog();
    };

    const handleAllCurrentPriceRowsSelected = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            if (response?.data?.rows && response.data.rows.some((r) => r.priceType.toLowerCase() === 'deviation')) {
                setAllDeviationRowsSelected(event.target.checked);
            } else if (
                response?.data?.rows &&
                response.data.rows.some((r) => r.priceType.toLowerCase() === 'market price')
            ) {
                setAllMarketPriceRowsSelected(event.target.checked);
            }
            setSelectedCurrentPrices([]);
        },
        [setAllMarketPriceRowsSelected, setAllDeviationRowsSelected, setSelectedCurrentPrices, response]
    );

    const handleAllMarketPriceRowsSelected = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            setAllMarketPriceRowsSelected(event.target.checked);
            setSelectedCurrentPrices([]);
        },
        [setAllMarketPriceRowsSelected, setSelectedCurrentPrices]
    );

    const handleAllDeviationRowsSelected = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            setAllDeviationRowsSelected(event.target.checked);
            setSelectedCurrentPrices([]);
        },
        [setAllDeviationRowsSelected, setSelectedCurrentPrices]
    );

    const handleMassActionSelected = (key: string) => {
        setSelectedMassActionType(key);
        massActionModal.handleMassActionModalOpen();
    };

    const handleExportPricesActionSelected = (key: string) => {
        if (key === MarketPriceExportEnum.EXPORTPRICES) {
            handlePriceSheetExport();
        } else if (key === MarketPriceExportEnum.EXPORTQUOTE) {
            setExportModalOpen(true);
        }
    };

    const isDisabledSelect = useCallback(
        (row: CurrentPricesData) => {
            let isDisabled = false;
            if (row.priceType.toLowerCase() !== 'deviation') {
                if (!isUserAdmin) return true;
                if (!allDeviationRowsSelected) {
                    if (
                        selectedCurrentPrices.length > 0 &&
                        selectedCurrentPrices.some((s) => s.priceType !== row.priceType)
                    ) {
                        isDisabled = true;
                    }
                } else {
                    isDisabled = true;
                }
            } else if (row.priceType.toLowerCase() !== 'market price') {
                if (!allMarketPriceRowsSelected) {
                    if (
                        selectedCurrentPrices.length > 0 &&
                        selectedCurrentPrices.some((s) => s.priceType !== row.priceType)
                    ) {
                        isDisabled = true;
                    }
                } else {
                    isDisabled = true;
                }
            }
            return isDisabled;
        },
        [allDeviationRowsSelected, allMarketPriceRowsSelected, selectedCurrentPrices, isUserAdmin]
    );

    const isSelectedIcon = useCallback(
        (row: CurrentPricesData) => {
            let isSelected = false;
            if (row.priceType.toLowerCase() === 'deviation') {
                if (!allDeviationRowsSelected) {
                    isSelected = selectedCurrentPrices.some((s) => row.id === s.id);
                } else {
                    isSelected = !selectedCurrentPrices.some((s) => row.id === s.id);
                }
            } else if (row.priceType.toLowerCase() === 'market price') {
                if (!allMarketPriceRowsSelected) {
                    isSelected = selectedCurrentPrices.some((s) => row.id === s.id);
                } else {
                    isSelected = !selectedCurrentPrices.some((s) => row.id === s.id);
                }
            }
            return isSelected;
        },
        [allDeviationRowsSelected, allMarketPriceRowsSelected, selectedCurrentPrices]
    );

    const modifyPriceMenuOptions: MenuOption[] = [
        ...(isMassEditMarketPricesAllowed
            ? [
                  {
                      key: ModifyPriceMassActionEnum.ADDPERCENTAGE,
                      value: ModifyPriceMassActionEnum.ADDPERCENTAGE,
                      action: handleMassActionSelected,
                      disabled:
                          allDeviationRowsSelected ||
                          (!allMarketPriceRowsSelected &&
                              selectedCurrentPrices.length > 0 &&
                              selectedCurrentPrices.some((s) => s.priceType.toLowerCase() !== 'market price')),
                  },
                  {
                      key: ModifyPriceMassActionEnum.ADDAMOUNT,
                      value: ModifyPriceMassActionEnum.ADDAMOUNT,
                      action: handleMassActionSelected,
                      disabled:
                          allDeviationRowsSelected ||
                          (!allMarketPriceRowsSelected &&
                              selectedCurrentPrices.length > 0 &&
                              selectedCurrentPrices.some((s) => s.priceType.toLowerCase() !== 'market price')),
                  },
                  {
                      key: ModifyPriceMassActionEnum.SETVALUE,
                      value: ModifyPriceMassActionEnum.SETVALUE,
                      action: handleMassActionSelected,
                      disabled:
                          allDeviationRowsSelected ||
                          (!allMarketPriceRowsSelected &&
                              selectedCurrentPrices.length > 0 &&
                              selectedCurrentPrices.some((s) => s.priceType.toLowerCase() !== 'market price')),
                  },
                  {
                      key: ModifyPriceMassActionEnum.RESETTOCURRENTTIER,
                      value: ModifyPriceMassActionEnum.RESETTOCURRENTTIER,
                      action: handleMassActionSelected,
                      disabled:
                          allDeviationRowsSelected ||
                          (!allMarketPriceRowsSelected &&
                              selectedCurrentPrices.length > 0 &&
                              selectedCurrentPrices.some((s) => s.priceType.toLowerCase() !== 'market price')),
                  },
                  {
                      key: ManagePricingMarketPriceDeleteMassActionEnum.DELETE,
                      value: ManagePricingMarketPriceDeleteMassActionEnum.DELETE,
                      action: handleMassActionSelected,
                      disabled:
                          allDeviationRowsSelected ||
                          (!allMarketPriceRowsSelected &&
                              selectedCurrentPrices.length > 0 &&
                              selectedCurrentPrices.some((s) => s.priceType.toLowerCase() !== 'market price')),
                  },
              ]
            : []),
        ...(isMassEditDeviationsAllowed
            ? [
                  {
                      key: DeviationDateMassActionEnum.ENDDATES,
                      value: DeviationDateMassActionEnum.ENDDATES,
                      action: handleMassActionSelected,
                      disabled:
                          allMarketPriceRowsSelected ||
                          (!allDeviationRowsSelected &&
                              selectedCurrentPrices.length > 0 &&
                              selectedCurrentPrices.some((s) => s.priceType.toLowerCase() !== 'deviation')),
                  },
              ]
            : []),
    ];

    const exportPricesOptions: MenuOption[] = [
        {
            key: MarketPriceExportEnum.EXPORTPRICES,
            value: MarketPriceExportEnum.EXPORTPRICES,
            action: handleExportPricesActionSelected,
            disabled: false,
        },
        {
            key: MarketPriceExportEnum.EXPORTQUOTE,
            value: MarketPriceExportEnum.EXPORTQUOTE,
            action: handleExportPricesActionSelected,
            disabled: false,
        },
    ];

    const columnConfig: GridColumns<CurrentPricesData> = [
        {
            headerName: 'id',
            field: 'id',
            hide: true,
            filterable: false,
            pinnable: false,
            width: 200,
        },
        {
            headerName:
                allMarketPriceRowsSelected || allDeviationRowsSelected ? 'Deselect Products' : 'Select Products',
            field: 'select',
            type: 'actions',
            hide: false,
            filterable: false,
            pinnable: false,
            width: 200,
            getActions: (params) => {
                return [
                    ...(isMassEditDeviationsAllowed || isMassEditMarketPricesAllowed
                        ? [
                              selectedCurrentPrices.map((s) => s.id).includes(params.row.id) ? (
                                  <GridActionsCellItem
                                      key={`selected-${params.row.id}`}
                                      icon={isSelectedIcon(params.row) ? <CheckCircleOutline /> : <AddCircle />}
                                      label='Selected'
                                      onClick={handleClick(params.row)}
                                      disabled={isDisabledSelect(params.row)}
                                  />
                              ) : (
                                  <GridActionsCellItem
                                      key={`select-${params.row.id}`}
                                      icon={isSelectedIcon(params.row) ? <CheckCircleOutline /> : <AddCircle />}
                                      label='Select'
                                      onClick={handleClick(params.row)}
                                      disabled={isDisabledSelect(params.row)}
                                  />
                              ),
                          ]
                        : []),
                    <GridActionsCellItem
                        key={`edit-${params.row.id}`}
                        icon={<Edit />}
                        label='Edit'
                        onClick={() =>
                            params.row.priceType.toLowerCase() === 'deviation'
                                ? openEditDeviationDialog(params.row)
                                : openEditMarketPriceDialog(params.row)
                        }
                    />,
                ];
            },
        },
        {
            headerName: 'Product',
            field: 'productId',
            hideable: false,
            pinnable: false,
            width: 200,
        },
        {
            headerName: 'Price Type',
            field: 'priceType',
            hideable: false,
            pinnable: false,
            width: 200,
        },
        {
            headerName: 'Price Group',
            field: 'priceGroup',
            hideable: false,
            pinnable: false,
            width: 200,
            renderCell: (params) => {
                if (params.value && endUserOptions.length > 0) {
                    const pricingGroupTooltip: EndUserTooltipItem[] = endUserOptions
                        .filter((endUser) => endUser.enduserpricinggroup === params.value)
                        .map((e) => {
                            return {
                                enduserName: e.endusername,
                                enduserNumber: e.endusernumber,
                            };
                        });
                    return (
                        <CustomWidthTooltip
                            title={
                                <>
                                    <div
                                        style={{
                                            fontSize: '.85rem',
                                            width: '100%',
                                            borderBottom: '1px solid #CCC',
                                            padding: '5px 2px',
                                        }}
                                    >
                                        <b>End Users</b>
                                    </div>
                                    <ul
                                        style={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            padding: '10px 20px',
                                            margin: '0',
                                        }}
                                    >
                                        {pricingGroupTooltip.map((enduser, index) => (
                                            <li
                                                key={enduser.enduserNumber}
                                                style={{
                                                    flex: '1 1 50%',
                                                    padding: '2px 0',
                                                    fontSize: '.85rem',
                                                }}
                                            >
                                                {`${enduser.enduserName} (${enduser.enduserNumber})`}
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            }
                        >
                            <span>{params.value}</span>
                        </CustomWidthTooltip>
                    );
                }
                return params.value;
            },
        },
        {
            headerName: 'End User',
            field: 'endUser',
            hideable: false,
            pinnable: false,
            width: 200,
            renderCell: (params) => {
                if (params.value && endUserOptions.length > 0) {
                    const endUserTooltip = endUserOptions.find(
                        (endUser) => endUser.endusernumber === params.row.endUserNumber
                    );
                    const fullAddress = `${endUserTooltip?.street}, ${endUserTooltip?.city}, ${endUserTooltip?.state}`;
                    return (
                        <Tooltip title={<span style={{ fontSize: '.85rem' }}>{fullAddress}</span>}>
                            <span>{params.value}</span>
                        </Tooltip>
                    );
                }
                return params.value;
            },
        },
        {
            headerName: 'End User Number',
            field: 'endUserNumber',
            hideable: false,
            pinnable: false,
            width: 200,
        },
        {
            headerName: 'EU Timing',
            field: 'endUserPriceTerms',
            hideable: false,
            pinnable: false,
            width: 100,
        },
        {
            headerName: 'EU Trigger',
            field: 'endUserPriceTrigger',
            hideable: false,
            pinnable: false,
            width: 100,
        },
        {
            headerName: 'Product Group',
            field: 'group',
            hideable: false,
            pinnable: false,
            width: 200,
        },
        {
            headerName: 'Type',
            field: 'type',
            hideable: false,
            pinnable: false,
            width: 100,
        },
        {
            headerName: 'Gauge',
            field: 'gauge',
            hideable: false,
            pinnable: false,
            type: 'number',
            width: 100,
        },
        {
            headerName: 'Length',
            field: 'length',
            hideable: false,
            pinnable: false,
            type: 'number',
            width: 100,
        },
        {
            headerName: 'Width',
            field: 'width',
            hideable: false,
            pinnable: false,
            type: 'number',
            width: 100,
        },
        {
            headerName: 'Unit Weight',
            field: 'unitWeight',
            hideable: false,
            pinnable: false,
            type: 'number',
            width: 100,
        },
        {
            headerName: 'Price',
            field: 'price',
            hideable: false,
            pinnable: false,
            type: 'number',
            valueFormatter: moneyFourDecimalFormatter,
            width: 100,
        },
        {
            headerName: 'Start Date',
            field: 'start',
            hideable: false,
            pinnable: false,
            valueGetter: (params) => parseDatePickerDate(params.value),
            valueFormatter: (params) => formatDatePickerStringWithoutTime(params.value),
            type: 'date',
            filterable: true,
            width: 200,
        },
        {
            headerName: 'End Date',
            field: 'end',
            hideable: false,
            pinnable: false,
            valueGetter: (params) => parseDatePickerDate(params.value),
            valueFormatter: (params) => formatDatePickerStringWithoutTime(params.value),
            type: 'date',
            filterable: true,
            width: 200,
            editable: true,
            renderEditCell: (params) => {
                return <GridEditDateCell {...params} minDate={new Date()} />;
            },
        },
        {
            headerName: 'Last Resin Change Date',
            field: 'lastResinChangeDate',
            hideable: false,
            pinnable: false,
            valueGetter: (params) => parseDatePickerDate(params.value),
            valueFormatter: (params) => formatDatePickerStringWithoutTime(params.value),
            type: 'date',
            filterable: true,
            width: 200,
        },
        {
            headerName: 'Price Lagger',
            field: 'priceLagger',
            hideable: false,
            pinnable: false,
            type: 'number',
            valueFormatter: moneyFourDecimalFormatter,
            width: 200,
        },
        {
            headerName: 'Expiring Soon',
            description: 'Deviation expires within 7 days',
            field: 'isExpiringSoon',
            hideable: false,
            pinnable: false,
            type: 'boolean',
            filterable: true,
            width: 100,
            renderCell: (params) => (params.value ? <WarningIcon color={'warning'} /> : ''),
        },
        {
            headerName: 'Status',
            field: 'status',
            hideable: false,
            pinnable: false,
            width: 150,
            renderCell: (params) =>
                params.value ? (
                    <Chip label={params.value} color={getStatusColumnColor(params.value)} variant='outlined' />
                ) : null,
        },
        {
            headerName: 'Approval Level',
            field: 'approver',
            hideable: false,
            pinnable: false,
            width: 150,
            renderCell: (params) => (
                <Tooltip title={params.row.approver}>
                    <span>
                        {params.row.status === ApprovalStatusEnum.NEEDSAPPROVAL
                            ? determineApprovalLevel(approverUsers, params.value)
                            : ''}
                    </span>
                </Tooltip>
            ),
        },
        {
            headerName: 'Last Updated',
            field: 'effectiveStart',
            hideable: false,
            pinnable: false,
            valueGetter: (params) => parseUtcDateTime(params.value),
            valueFormatter: (params) => formatUtcDateTimeToLocalDateTimeString(params.value),
            type: 'dateTime',
            width: 200,
        },
        {
            headerName: 'TTM Pounds',
            field: 'ttmPounds',
            hideable: false,
            pinnable: false,
            type: 'number',
            width: 200,
        },
        {
            headerName: 'National Account',
            field: 'isNationalAccount',
            hideable: false,
            pinnable: false,
            width: 100,
            type: 'boolean',
        },
        {
            field: 'actions',
            type: 'actions',
            width: 80,
            getActions: (params) => [
                <GridActionsCellItem
                    key={`delete-${params.row.id}`}
                    icon={<Delete />}
                    disabled={
                        params.row.priceType.toLowerCase() === 'deviation' ||
                        !(distributor.matchingCustomer?.canEdit || isExpireMarketPriceAllowed)
                    }
                    label='Delete'
                    onClick={() => openDeleteDialog(params.row)}
                />,
                <GridActionsCellItem
                    icon={<History />}
                    label='Price History'
                    onClick={() => {
                        resetPageNumber();
                        openPriceHistoryDialog(params.row);
                    }}
                />,
            ],
        },
    ];

    const todayMidnight = new Date();
    todayMidnight.setHours(0, 0, 0, 0);

    const customToolbarChildren = [
        ...(isMassEditDeviationsAllowed || isMassEditMarketPricesAllowed
            ? [
                  <MassActionToolbar
                      key={'modify-price'}
                      menuOptions={modifyPriceMenuOptions}
                      title={'Mass actions'}
                      disabled={
                          !allMarketPriceRowsSelected &&
                          selectedCurrentPrices.length === 0 &&
                          !allDeviationRowsSelected &&
                          selectedCurrentPrices.length === 0
                      }
                      allowSelectAll={true}
                      customSelectAll={
                          !filtersApplied?.items.some((f) => f.columnField === 'priceType') && (
                              <>
                                  <FormControlLabel
                                      control={
                                          <MuiSwitch
                                              onChange={handleAllMarketPriceRowsSelected}
                                              disabled={
                                                  allDeviationRowsSelected ||
                                                  (!allMarketPriceRowsSelected &&
                                                      selectedCurrentPrices.length > 0 &&
                                                      selectedCurrentPrices.some(
                                                          (s) => s.priceType.toLowerCase() !== 'market price'
                                                      ))
                                              }
                                          />
                                      }
                                      label='Select All Market Prices'
                                  />
                                  <FormControlLabel
                                      control={
                                          <MuiSwitch
                                              onChange={handleAllDeviationRowsSelected}
                                              disabled={
                                                  allMarketPriceRowsSelected ||
                                                  (!allDeviationRowsSelected &&
                                                      selectedCurrentPrices.length > 0 &&
                                                      selectedCurrentPrices.some(
                                                          (s) => s.priceType.toLowerCase() !== 'deviation'
                                                      ))
                                              }
                                          />
                                      }
                                      label='Select All Deviations'
                                  />
                              </>
                          )
                      }
                      disabledSelectAllButton={response?.data?.rows.length === 0}
                      handleAllRowsSelected={handleAllCurrentPriceRowsSelected}
                      selectedMassActionType={selectedMassActionType}
                      handleOpen={massActionModal.handleMassActionModalOpen}
                      handleClose={massActionModal.handleMassActionModalClose}
                      isOpen={massActionModal.isMassActionModalopen}
                  >
                      {(selectedMassActionType === ModifyPriceMassActionEnum.ADDPERCENTAGE && (
                          <ChangeByPercent
                              defaultValues={{
                                  type: ModifyPriceMassActionEnum.ADDPERCENTAGE,
                                  accountNumber: distributor.matchingCustomer.customerNumber,
                                  actionForm: {
                                      percentChange: '',
                                      comments: '',
                                      start: formatLocalDateToLocalISODateTimeString(new Date().toLocaleDateString()),
                                  },
                              }}
                              onSubmit={submitChangeModifyPriceByPercent.onSubmit}
                              isLoading={submitChangeModifyPriceByPercent.isLoading}
                              allRowsSelected={allMarketPriceRowsSelected}
                              tableDataContext={managePricingContext}
                              columnConfig={columnConfig}
                              rowCount={selectedCurrentPrices.length}
                              validate={{ isBetweenNeg1AndInclusivePos1 }}
                          />
                      )) ||
                          (selectedMassActionType === ModifyPriceMassActionEnum.ADDAMOUNT && (
                              <ChangeByDollarAmount
                                  defaultValues={{
                                      type: ModifyPriceMassActionEnum.ADDAMOUNT,
                                      accountNumber: distributor.matchingCustomer.customerNumber,
                                      actionForm: {
                                          amountIncrease: '',
                                          comments: '',
                                          start: formatLocalDateToLocalISODateTimeString(
                                              new Date().toLocaleDateString()
                                          ),
                                      },
                                  }}
                                  onSubmit={submitChangeModifyPriceByDollarAmount.onSubmit}
                                  isLoading={submitChangeModifyPriceByDollarAmount.isLoading}
                                  allRowsSelected={allMarketPriceRowsSelected}
                                  tableDataContext={managePricingContext}
                                  columnConfig={columnConfig}
                                  rowCount={selectedCurrentPrices.length}
                              />
                          )) ||
                          (selectedMassActionType === ModifyPriceMassActionEnum.SETVALUE && (
                              <ChangeBySetValue
                                  defaultValues={{
                                      type: ModifyPriceMassActionEnum.SETVALUE,
                                      accountNumber: distributor.matchingCustomer.customerNumber,
                                      actionForm: {
                                          value: '',
                                          comments: '',
                                          start: formatLocalDateToLocalISODateTimeString(
                                              new Date().toLocaleDateString()
                                          ),
                                      },
                                  }}
                                  onSubmit={submitChangeModifyPriceBySetValue.onSubmit}
                                  isLoading={submitChangeModifyPriceBySetValue.isLoading}
                                  allRowsSelected={allMarketPriceRowsSelected}
                                  tableDataContext={managePricingContext}
                                  columnConfig={columnConfig}
                                  rowCount={selectedCurrentPrices.length}
                                  validate={{ isPositiveOrZero }}
                              />
                          )) ||
                          (selectedMassActionType === DeviationDateMassActionEnum.ENDDATES && (
                              <ChangeDeviationDate
                                  defaultValues={{
                                      type: DeviationDateMassActionEnum.ENDDATES,
                                      accountNumber: distributor.matchingCustomer.customerNumber,
                                      actionForm: { endDate: '', comments: '' },
                                  }}
                                  onSubmit={submitChangeDeviationDate.onSubmit}
                                  isLoading={submitChangeDeviationDate.isLoading}
                                  allRowsSelected={allDeviationRowsSelected}
                                  tableDataContext={managePricingContext}
                                  columnConfig={columnConfig}
                                  rowCount={selectedCurrentPrices.length}
                                  validate={{ isValidDate }}
                              />
                          )) ||
                          (selectedMassActionType === ModifyPriceMassActionEnum.RESETTOCURRENTTIER && (
                              <NoInput
                                  isLoading={submitNoInputResetToCurrentTier.isLoading}
                                  onSubmit={submitNoInputResetToCurrentTier.onSubmit}
                                  tableDataContext={managePricingContext}
                                  allRowsSelected={allMarketPriceRowsSelected}
                                  rowCount={selectedCurrentPrices.length}
                                  columnConfig={columnConfig}
                                  defaultValues={{
                                      type: ModifyPriceMassActionEnum.RESETTOCURRENTTIER,
                                      actionForm: undefined,
                                      accountNumber: distributor.matchingCustomer.customerNumber,
                                  }}
                              />
                          )) ||
                          (selectedMassActionType === ManagePricingMarketPriceDeleteMassActionEnum.DELETE && (
                              <NoInput
                                  isLoading={submitMassDeleteMarketPrices.isLoading}
                                  onSubmit={submitMassDeleteMarketPrices.onSubmit}
                                  tableDataContext={managePricingContext}
                                  allRowsSelected={allMarketPriceRowsSelected}
                                  rowCount={selectedCurrentPrices.length}
                                  columnConfig={columnConfig}
                                  defaultValues={{
                                      type: ManagePricingMarketPriceDeleteMassActionEnum.DELETE,
                                      actionForm: undefined,
                                      accountNumber: distributor.matchingCustomer.customerNumber,
                                  }}
                              />
                          ))}
                  </MassActionToolbar>,
              ]
            : []),
        <ExportIconDropdownMenu
            key={'dropdown-export-prices'}
            title={'Export Prices'}
            menuOptions={exportPricesOptions}
        />,
    ];

    return (
        <>
            <CurrentPricesLayout matchingCustomer={distributor.matchingCustomer}>
                <Switch>
                    <Route path={ROUTES.addDeviations}>
                        <AddDeviations customerName={distributor.matchingCustomer.customerName} />
                    </Route>

                    <Route path={ROUTES.addProducts}>
                        {isAddProductAllowed ? (
                            <ProvideSelectedItems selectedItemsContext={selectedProductsContext}>
                                <AddProducts priceTier={distributor.matchingCustomer.priceTier} />
                            </ProvideSelectedItems>
                        ) : (
                            <Redirect to={managePricingRoute.path} />
                        )}
                    </Route>

                    <Route path={ROUTES.tierSetUp}>
                        <TierSetUpResolver />
                    </Route>

                    <Route path={ROUTES.managePricing}>
                        <CurrentPricesSummary matchingCustomer={distributor.matchingCustomer} />
                        <TableDataPage
                            title='Manage Pricing'
                            columnConfig={columnConfig}
                            tableDataContext={managePricingContext}
                            config={{
                                hideExport: true,
                                manualSaveProps: {
                                    hideManualSave: false,
                                    handleIsEditableRow,
                                    editButtonText: 'Edit Dev. End Date',
                                },
                                pinnedColumnsProps: {
                                    pinnedColumnsStorage: 'active-manage-pricing-pinned-columns',
                                    pinAllColumns: true,
                                    pinnable: true,
                                    pinnedColumnsDefault: ['select'],
                                },
                            }}
                            customToolbarChildren={customToolbarChildren}
                        />
                        <Dialog open={deleteDialogState.open} aria-labelledby='responsive-dialog-title'>
                            <DialogTitle id='responsive-dialog-title'>{'Are you sure?'}</DialogTitle>
                            <DialogContent>
                                <DialogContentText>Expire this market price and associated data?</DialogContentText>
                            </DialogContent>
                            <DialogActions>
                                <Button autoFocus onClick={closeDeleteDialog}>
                                    No
                                </Button>
                                <Button onClick={confirmDelete} autoFocus>
                                    Yes
                                </Button>
                            </DialogActions>
                        </Dialog>
                        <EditMarketPriceFormDialog
                            key={`edit-market-price-dialog-${editMarketPriceDialogState.open}`}
                            closeEditMarketPriceDialog={closeEditMarketPriceDialog}
                            open={editMarketPriceDialogState.open}
                            row={editMarketPriceDialogState.row}
                        />
                        <EditDeviationFormDialog
                            key={`edit-deviation-dialog-${editDeviationDialogState.open}`}
                            closeEditDeviationDialog={closeEditDeviationDialog}
                            open={editDeviationDialogState.open}
                            row={editDeviationDialogState.row}
                        />
                        <PriceHistoryModal
                            key={priceHistoryDialogState?.row?.id}
                            open={priceHistoryDialogState.open}
                            rows={priceHistoryData}
                            onClose={closePriceHistoryDialog}
                            totalRows={priceHistoryResponse.data?.totalRows || 0}
                            isLoading={priceHistoryResponse.isLoading}
                            isFetching={priceHistoryResponse.isFetching}
                            serverSideProps={priceHistoryServerSideProps}
                            sortModel={sortModel}
                            onSortModelChange={handleSortModelChange}
                        />
                        <ExportQuoteModal
                            open={exportModalOpen}
                            matchingCustomer={distributor.matchingCustomer}
                            context={managePricingContext}
                            endUserOptions={endUserOptions}
                            onClose={() => setExportModalOpen(false)}
                        />
                    </Route>
                </Switch>
            </CurrentPricesLayout>
        </>
    );
}
